import { useCallback, useEffect, useMemo, useState } from 'react';
import { useNavigate, useOutletContext, useParams } from 'react-router-dom';
import { useMemberAuth } from '../../../contexts/MemberAuthContext';
import { mfetch, MemberApiError } from '../api';
import Icon from '../design/Icon';
import { inr } from '../design/atoms';
import CoachCard, { CoachCardData, coachMeta, nextFreeLabel } from '../components/CoachCard';
import CoachStep from '../components/CoachStep';
import type { MemberGym } from '../hooks/useMemberGyms';

/* The gym's coaches, therapists and consultants — a gallery from the Book
   tab, and a profile that can start a purchase ("Train with Priya"). */

interface OutletCtx { activeGym: MemberGym | null; activeGymId: string | null }
interface Delivers { classId: string; name: string; bookableType: string; scheduleMode: string }
interface Coach extends CoachCardData { role: string; delivers: Delivers[]; gender: string | null }

const TYPE_LABEL: Record<string, string> = { pt: 'Personal training', spa: 'Spa & recovery', consult: 'Consults', class: 'Classes' };

export function MemberCoachesPage() {
  const { token } = useMemberAuth();
  const { activeGymId } = useOutletContext<OutletCtx>();
  const navigate = useNavigate();
  const [data, setData] = useState<{ today: string; coaches: Coach[]; facets: { bookableTypes: string[] } } | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [type, setType] = useState<string | null>(null);

  const load = useCallback(() => {
    if (!token || !activeGymId) return;
    setError(null);
    mfetch(token, '/member/coaches', { params: { gymId: activeGymId } })
      .then(r => setData(r.data))
      .catch(e => setError(e instanceof MemberApiError ? e.message : 'Could not load coaches'));
  }, [token, activeGymId]);
  useEffect(load, [load]);

  const shown = useMemo(
    () => (data?.coaches ?? []).filter(c => !type || c.delivers.some(d => d.bookableType === type)),
    [data, type]
  );

  return (
    <div className="mpage">
      <div className="row" style={{ gap: 10, paddingTop: 8 }}>
        <button className="icon-btn" onClick={() => navigate(-1)} aria-label="Back"><Icon n="left" s={19} /></button>
        <div className="dp dp-l">Coaches</div>
      </div>

      {error && (
        <div className="card flat" style={{ padding: 16 }}>
          <div className="col" style={{ gap: 8 }}>
            <div className="mid wrap">{error}</div>
            <button className="btn ghost sm" style={{ alignSelf: 'flex-start' }} onClick={load}>Retry</button>
          </div>
        </div>
      )}
      {!data && !error && [0, 1].map(i => <div key={i} className="skel" style={{ height: 380, borderRadius: 'var(--r-l)' }} />)}

      {data && data.facets.bookableTypes.length > 1 && (
        <div className="hscroll">
          <button className={`chip${type ? '' : ' on'}`} onClick={() => setType(null)}>Everyone</button>
          {data.facets.bookableTypes.map(t => (
            <button key={t} className={`chip${type === t ? ' on' : ''}`} onClick={() => setType(t)}>{TYPE_LABEL[t] ?? t}</button>
          ))}
        </div>
      )}

      {data && shown.length === 0 && (
        <div className="card flat" style={{ padding: 16 }}><div className="sub wrap">Coach profiles will appear here once your gym adds them.</div></div>
      )}

      {shown.map(c => (
        <button key={c.id} type="button" style={{ textAlign: 'left' }} onClick={() => navigate(`/app/coaches/${c.id}`)}>
          <CoachCard coach={c} today={data?.today} flippable={false} />
        </button>
      ))}
    </div>
  );
}

export function MemberCoachPage() {
  const { token } = useMemberAuth();
  const { activeGymId } = useOutletContext<OutletCtx>();
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const [data, setData] = useState<{ today: string; coach: Coach; offerings: Array<{ kind: 'plan' | 'pack'; id: string; name: string; price: number }> } | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [buying, setBuying] = useState<{ kind: 'plan' | 'pack'; id: string } | null>(null);

  useEffect(() => {
    if (!token || !activeGymId || !id) return;
    mfetch(token, `/member/coaches/${id}`, { params: { gymId: activeGymId } })
      .then(r => setData(r.data))
      .catch(e => setError(e instanceof MemberApiError ? e.message : 'Could not load this coach'));
  }, [token, activeGymId, id]);

  const c = data?.coach;
  const meta = c ? coachMeta(c) : '';

  return (
    <div className="mpage">
      <div className="row" style={{ gap: 10, paddingTop: 8 }}>
        <button className="icon-btn" onClick={() => navigate(-1)} aria-label="Back"><Icon n="left" s={19} /></button>
        <div className="dp dp-m trunc">{c?.name ?? 'Coach'}</div>
      </div>

      {error && <div className="card flat" style={{ padding: 16 }}><div className="sub wrap">{error}</div></div>}
      {!data && !error && <div className="skel" style={{ height: 460, borderRadius: 'var(--r-l)' }} />}

      {c && (
        <>
          <CoachCard coach={c} today={data!.today} flippable={false} />

          {c.nextFree && (
            <div className="card tint" style={{ padding: 14 }}>
              <div className="between">
                <div className="col" style={{ gap: 2 }}>
                  <span className="lbl-d">Next free</span>
                  <span className="mid">{nextFreeLabel(c.nextFree, data!.today)}</span>
                </div>
                <button className="btn sm" onClick={() => navigate('/app/book')}>Book <Icon n="right" s={15} /></button>
              </div>
            </div>
          )}

          {c.bio && (
            <div className="col" style={{ gap: 6 }}>
              <span className="lbl">About</span>
              <div className="sub wrap" style={{ whiteSpace: 'pre-line' }}>{c.bio}</div>
              {meta && <div className="tiny">{meta}</div>}
            </div>
          )}

          {!!c.certifications?.length && (
            <div className="card" style={{ padding: 14 }}>
              <span className="lbl">Certifications</span>
              <div className="col" style={{ gap: 8, marginTop: 10 }}>
                {c.certifications.map((x, i) => (
                  <div key={i} className="cert">
                    <span className="li gold"><Icon n="medal" s={16} /></span>
                    <div className="col" style={{ minWidth: 0 }}>
                      <span className="mid trunc">{x.name}</span>
                      {(x.issuer || x.year) && <span className="tiny trunc">{[x.issuer, x.year].filter(Boolean).join(' · ')}</span>}
                    </div>
                  </div>
                ))}
              </div>
            </div>
          )}

          {c.delivers.length > 0 && (
            <div className="col" style={{ gap: 6 }}>
              <span className="lbl">Sessions</span>
              <div className="row" style={{ flexWrap: 'wrap', gap: 6 }}>
                {c.delivers.map(d => <span key={d.classId} className="tag">{d.name}</span>)}
              </div>
            </div>
          )}

          {data!.offerings.length > 0 && (
            <div className="col" style={{ gap: 10 }}>
              <span className="lbl">Train with {c.name.split(' ')[0]}</span>
              {data!.offerings.map(o => (
                <div key={`${o.kind}:${o.id}`} className="card" style={{ padding: 14 }}>
                  <div className="between">
                    <div className="col" style={{ gap: 2, minWidth: 0 }}>
                      <span className="mid trunc">{o.name}</span>
                      <span className="tiny">{o.kind === 'plan' ? 'Membership plan' : 'Training'}</span>
                    </div>
                    <div className="col" style={{ gap: 6, alignItems: 'flex-end' }}>
                      <span className="num">{inr(o.price)}</span>
                      <button className="btn gold sm" onClick={() => setBuying({ kind: o.kind, id: o.id })}>Choose</button>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          )}
        </>
      )}

      {buying && token && activeGymId && c && (
        <CoachStep
          token={token}
          gymId={activeGymId}
          kind={buying.kind}
          id={buying.id}
          current={{ trainerId: c.id, trainerName: c.name, price: data!.offerings.find(o => o.id === buying.id)?.price ?? 0 }}
          onClose={() => setBuying(null)}
          onChoose={choice => { const add = { ...buying, coach: choice }; setBuying(null); navigate('/app/plans', { state: { add } }); }}
        />
      )}
    </div>
  );
}
